/* RESPONSIBLE TEAM: team-reporting */

import type {
  ChartConfig,
  ChartData,
  ChartDataResponse,
  FractionUnit,
  MetricValue,
  RenderContext,
} from '@/features/reporting-charts/types';
import {
  isDimensionCell,
  isDualQueryCell,
  isGroupedData,
  isMetricValueDualQuery,
} from '@/features/reporting-charts/types';
import {
  applyFractionUnit,
  findPeriod,
  formatValue,
} from '@/features/reporting-charts/transforms/utils';

export interface CounterValue {
  value: number | null;
  previousValue: number | null;
  formattedValue: string;
  formattedDataLabel: string | null;
  absoluteChange: number | null;
  formattedAbsoluteChange: string | null;
  changeDirection: 'up' | 'down' | 'none';
  improvementDirection: 'increasing' | 'decreasing';
  formattedTarget: string | null;
}

export function buildCounterValue(
  data: ChartDataResponse,
  config: ChartConfig,
  renderContext: RenderContext,
): CounterValue {
  const current = findPeriod(data, 'current');
  const previous = findPeriod(data, 'previous');

  const metricMeta = current?.metadata.metricMetadata[0];
  const unit = metricMeta?.unit ?? 'value';
  const improvementDirection = metricMeta?.improvementDirection ?? 'increasing';

  const fractionUnit = metricMeta?.fractionUnit;
  const rawMetric = extractFirstMetric(current);
  const value = rawMetric !== null ? getNumericValue(rawMetric, fractionUnit) : null;
  const previousValue = extractFirstValue(previous, fractionUnit);

  const absoluteChange = calculateAbsoluteChange(value, previousValue);
  const changeDirection = getChangeDirection(absoluteChange);
  const target = config.visualizationOptions?.target?.value ?? null;

  // Data labels: only show when enabled AND metric is a DualQueryCell (has numerator/denominator)
  const showDataLabels = config.chartSeries?.[0]?.showDataLabels ?? false;
  const formattedDataLabel =
    showDataLabels && rawMetric !== null && isMetricValueDualQuery(rawMetric)
      ? formatDataLabel(rawMetric, renderContext)
      : null;

  return {
    value,
    previousValue,
    formattedValue: formatValue(value, unit, renderContext),
    formattedDataLabel,
    absoluteChange,
    formattedAbsoluteChange:
      absoluteChange !== null ? formatValue(Math.abs(absoluteChange), unit, renderContext) : null,
    changeDirection,
    improvementDirection,
    formattedTarget: target !== null ? formatValue(target, unit, renderContext) : null,
  };
}

function extractFirstMetric(chartData: ChartData | null): MetricValue | null {
  if (!chartData || chartData.data.length === 0) return null;

  // Counter charts use FlatData, but handle GroupedData for robustness
  if (isGroupedData(chartData.data)) {
    const firstRow = chartData.data[0];
    if (firstRow.length === 0) return null;

    // Iterate through cells to find first metric (skip dimension cells)
    for (const cell of firstRow) {
      if (isDimensionCell(cell)) continue;
      if (typeof cell === 'number') return cell;
      if (isDualQueryCell(cell)) return cell;
    }
    return null;
  }

  return chartData.data[0];
}

function getNumericValue(metric: MetricValue, fractionUnit?: FractionUnit): number {
  return isMetricValueDualQuery(metric) ? applyFractionUnit(metric, fractionUnit) : metric;
}

function extractFirstValue(
  chartData: ChartData | null,
  fractionUnit?: FractionUnit,
): number | null {
  const metric = extractFirstMetric(chartData);
  return metric !== null ? getNumericValue(metric, fractionUnit) : null;
}

function calculateAbsoluteChange(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null) return null;
  return current - previous;
}

function getChangeDirection(change: number | null): 'up' | 'down' | 'none' {
  if (change === null || change === 0) return 'none';
  return change > 0 ? 'up' : 'down';
}

function formatDataLabel(
  metric: { numeratorValue: number; denominatorValue: number },
  renderContext: RenderContext,
): string {
  const formatter = new Intl.NumberFormat(renderContext.locale);
  return `${formatter.format(metric.numeratorValue)} out of ${formatter.format(metric.denominatorValue)}`;
}
